import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { AppContext } from "../../../context/AppContext";

const PRIMARY = "#1565C0";
const BLACK = "#111827";

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");

  for (let cookie of cookies) {
    const trimmed = cookie.trim();

    if (trimmed.startsWith(name + "=")) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }

  return null;
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor: "#fff",
  },
};

const headCellSx = {
  bgcolor: PRIMARY,
  color: "#fff",
  fontWeight: 800,
  fontSize: 13,
  py: 1.15,
  borderBottom: "0",
};

const bodyCellSx = {
  fontSize: 13,
  color: "#334155",
  borderBottom: "1px solid #eef2f7",
};

const emptyDash = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const SectionTitle = ({ title, subtitle }) => (
  <Box sx={{ mb: 1.8 }}>
    <Typography sx={{ fontWeight: 800, fontSize: 18, color: BLACK }}>
      {title}
    </Typography>

    {subtitle && (
      <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.3 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

const InfoRow = ({ label, value, strong = false }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      gap: 2,
      py: 0.8,
      borderBottom: "1px solid #e5e7eb",
    }}
  >
    <Typography sx={{ fontSize: 13, color: "#64748b" }}>{label}</Typography>

    <Typography
      sx={{
        fontSize: 13,
        color: BLACK,
        fontWeight: strong ? 800 : 600,
        textAlign: "right",
      }}
    >
      {emptyDash(value)}
    </Typography>
  </Box>
);

const normalizeSections = (data) => {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.sections)
    ? data.sections
    : Array.isArray(data?.secciones)
    ? data.secciones
    : Array.isArray(data?.results)
    ? data.results
    : [];

  return list
    .map((s) => {
      const sectionId = String(s.id || "");

      const categories = Array.isArray(s.categories)
        ? s.categories
        : Array.isArray(s.categorias)
        ? s.categorias
        : [];

      return {
        id: sectionId,
        name: s.name || s.nombre || s.section_name || "",
        categories: categories
          .map((c) => ({
            id: String(c.id || ""),
            name: c.name || c.nombre || c.category_name || "",
            section_id: String(c.section_id || c.section || c.section_id_id || sectionId || ""),
          }))
          .filter((c) => c.id && c.name),
      };
    })
    .filter((s) => s.id && s.name);
};

const normalizeCategories = (data) => {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.categories)
    ? data.categories
    : Array.isArray(data?.categorias)
    ? data.categorias
    : Array.isArray(data?.results)
    ? data.results
    : [];

  return list
    .map((c) => ({
      id: String(c.id || ""),
      name: c.name || c.nombre || c.category_name || "",
      section_id: String(
        c.section_id ||
          c.section ||
          c.section_id_id ||
          c.section?.id ||
          c.seccion_id ||
          c.seccion ||
          ""
      ),
    }))
    .filter((c) => c.id && c.name);
};

const mergeSectionsWithCategories = (sections, categories) => {
  if (!Array.isArray(sections)) return [];

  return sections.map((s) => {
    const sectionCategories = Array.isArray(s.categories) ? s.categories : [];

    const extraCategories = categories.filter(
      (c) => String(c.section_id) === String(s.id)
    );

    const all = [...sectionCategories, ...extraCategories];
    const seen = new Set();

    return {
      ...s,
      categories: all.filter((c) => {
        const key = String(c.id);

        if (!key || seen.has(key)) return false;

        seen.add(key);
        return true;
      }),
    };
  });
};

const EditClient = ({
  personaId,
  persona,
  formData,
  setFormData,
  saving,
  onSave,
  onRefresh,
}) => {
  const { authFetch, sections: contextSections, refreshSections } = useContext(AppContext);

  const [sections, setSections] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [loadingDiscounts, setLoadingDiscounts] = useState(false);
  const [savingDiscount, setSavingDiscount] = useState(false);

  const [discountForm, setDiscountForm] = useState({
    tipo: "SECCION",
    section_id: "",
    category_id: "",
    porcentaje: "",
    activo: true,
    fecha_inicio: "",
    fecha_fin: "",
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const categories = useMemo(() => {
    if (!discountForm.section_id) return [];

    const section = sections.find(
      (s) => String(s.id) === String(discountForm.section_id)
    );

    return section?.categories || [];
  }, [sections, discountForm.section_id]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDiscountChange = (field, value) => {
    setDiscountForm((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (field === "tipo") {
        next.section_id = "";
        next.category_id = "";
      }

      if (field === "section_id") {
        next.category_id = "";
      }

      return next;
    });
  };

  const loadSections = async () => {
    try {
      const localSections = normalizeSections(contextSections);

      if (localSections.length > 0) {
        const hasLocalCategories = localSections.some((s) => s.categories.length > 0);

        if (hasLocalCategories) {
          setSections(localSections);
          return;
        }
      }

      if (refreshSections) {
        await refreshSections();
      }

      const sectionsResponse = await authFetch("/categorias/secciones/", {
        credentials: "include",
      });

      const sectionsData = await sectionsResponse.json().catch(() => []);
      const loadedSections = sectionsResponse.ok ? normalizeSections(sectionsData) : localSections;

      let loadedCategories = [];

      try {
        const categoriesResponse = await authFetch("/categorias/categorias/", {
          credentials: "include",
        });

        const categoriesData = await categoriesResponse.json().catch(() => []);
        loadedCategories = categoriesResponse.ok ? normalizeCategories(categoriesData) : [];
      } catch {
        loadedCategories = [];
      }

      setSections(mergeSectionsWithCategories(loadedSections, loadedCategories));
    } catch {
      setSections([]);
    }
  };

  const loadDiscounts = async () => {
    if (!personaId) return;

    setLoadingDiscounts(true);

    try {
      const res = await authFetch(`/clientes/descuentos/${personaId}/`, {
        credentials: "include",
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok && data.ok) {
        setDiscounts(Array.isArray(data.descuentos) ? data.descuentos : []);
      } else {
        setDiscounts([]);
      }
    } catch {
      setDiscounts([]);
    } finally {
      setLoadingDiscounts(false);
    }
  };

  useEffect(() => {
    loadSections();
  }, [contextSections]);

  useEffect(() => {
    loadDiscounts();
  }, [personaId]);

  const saveDiscount = async () => {
    if (savingDiscount) return;

    if (!discountForm.porcentaje) {
      setSnackbar({
        open: true,
        type: "error",
        message: "Debe ingresar el porcentaje de descuento.",
      });
      return;
    }

    if (discountForm.tipo === "SECCION" && !discountForm.section_id) {
      setSnackbar({
        open: true,
        type: "error",
        message: "Debe seleccionar una sección.",
      });
      return;
    }

    if (discountForm.tipo === "CATEGORIA" && !discountForm.category_id) {
      setSnackbar({
        open: true,
        type: "error",
        message: "Debe seleccionar una categoría.",
      });
      return;
    }

    setSavingDiscount(true);

    const payload = {
      tipo: discountForm.tipo,
      section_id: discountForm.tipo === "SECCION" ? discountForm.section_id : "",
      category_id: discountForm.tipo === "CATEGORIA" ? discountForm.category_id : "",
      porcentaje: discountForm.porcentaje,
      activo: !!discountForm.activo,
      fecha_inicio: discountForm.fecha_inicio || null,
      fecha_fin: discountForm.fecha_fin || null,
    };

    try {
      const csrf = getCSRFToken();

      const res = await authFetch(`/clientes/descuentos/${personaId}/guardar/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRFToken": csrf } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.ok) {
        setSnackbar({
          open: true,
          type: "error",
          message:
            typeof data.error === "string"
              ? data.error
              : "No se pudo guardar el descuento.",
        });
        return;
      }

      setSnackbar({
        open: true,
        type: "success",
        message: data.mensaje || "Descuento guardado correctamente.",
      });

      setDiscountForm({
        tipo: "SECCION",
        section_id: "",
        category_id: "",
        porcentaje: "",
        activo: true,
        fecha_inicio: "",
        fecha_fin: "",
      });

      await loadDiscounts();

      if (onRefresh) {
        await onRefresh();
      }
    } catch {
      setSnackbar({
        open: true,
        type: "error",
        message: "Error de conexión al guardar el descuento.",
      });
    } finally {
      setSavingDiscount(false);
    }
  };

  const deleteDiscount = async (discountId) => {
    if (!discountId) return;

    try {
      const csrf = getCSRFToken();

      const res = await authFetch(`/clientes/descuentos/eliminar/${discountId}/`, {
        method: "DELETE",
        headers: {
          ...(csrf ? { "X-CSRFToken": csrf } : {}),
        },
        credentials: "include",
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.ok) {
        setSnackbar({
          open: true,
          type: "error",
          message:
            typeof data.error === "string"
              ? data.error
              : "No se pudo eliminar el descuento.",
        });
        return;
      }

      setSnackbar({
        open: true,
        type: "success",
        message: data.mensaje || "Descuento eliminado correctamente.",
      });

      await loadDiscounts();

      if (onRefresh) {
        await onRefresh();
      }
    } catch {
      setSnackbar({
        open: true,
        type: "error",
        message: "Error de conexión al eliminar el descuento.",
      });
    }
  };

  return (
    <Box>
      <Paper
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid #e5e7eb",
          boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
          mb: 2.5,
        }}
      >
        <SectionTitle
          title="Editar cliente"
          subtitle="Información general, roles y datos de contacto"
        />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth sx={inputSx}>
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={formData.tipo}
                onChange={(e) => handleChange("tipo", e.target.value)}
              >
                <MenuItem value="N">Natural</MenuItem>
                <MenuItem value="J">Jurídica</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormGroup row sx={{ gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.es_cliente}
                    onChange={(e) => handleChange("es_cliente", e.target.checked)}
                  />
                }
                label="Cliente"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.es_proveedor}
                    onChange={(e) => handleChange("es_proveedor", e.target.checked)}
                  />
                }
                label="Proveedor"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.es_empleado}
                    onChange={(e) => handleChange("es_empleado", e.target.checked)}
                  />
                }
                label="Empleado"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.es_extranjero}
                    onChange={(e) => handleChange("es_extranjero", e.target.checked)}
                  />
                }
                label="Extranjero"
              />
            </FormGroup>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Razón social / Nombre"
              fullWidth
              required
              value={formData.razon_social}
              onChange={(e) => handleChange("razon_social", e.target.value)}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Nombre comercial"
              fullWidth
              value={formData.nombre_comercial}
              onChange={(e) => handleChange("nombre_comercial", e.target.value)}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Cédula"
              fullWidth
              value={formData.cedula}
              onChange={(e) => handleChange("cedula", e.target.value)}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="RUC"
              fullWidth
              value={formData.ruc}
              onChange={(e) => handleChange("ruc", e.target.value)}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Teléfono"
              fullWidth
              value={formData.telefono}
              onChange={(e) => handleChange("telefono", e.target.value)}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Correo"
              fullWidth
              value={formData.correo}
              onChange={(e) => handleChange("correo", e.target.value)}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Dirección"
              fullWidth
              multiline
              rows={3}
              value={formData.direccion}
              onChange={(e) => handleChange("direccion", e.target.value)}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Button
                variant="contained"
                startIcon={<SaveOutlinedIcon />}
                onClick={onSave}
                disabled={saving}
                sx={{
                  textTransform: "none",
                  backgroundColor: PRIMARY,
                  borderRadius: 2,
                  px: 2.5,
                  fontWeight: 700,
                  boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
                  "&:hover": {
                    backgroundColor: "#0D47A1",
                  },
                }}
              >
                {saving ? "Guardando..." : "Guardar cambios"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid #e5e7eb",
          boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
          mb: 2.5,
        }}
      >
        <SectionTitle
          title="Descuentos del cliente"
          subtitle="Asigna descuentos por sección o por categoría de productos"
        />

        {!formData.es_cliente && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            Esta persona no está marcada como cliente. Marca Cliente para poder aplicar descuentos.
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={inputSx}>
              <InputLabel>Tipo descuento</InputLabel>
              <Select
                label="Tipo descuento"
                value={discountForm.tipo}
                onChange={(e) => handleDiscountChange("tipo", e.target.value)}
                disabled={!formData.es_cliente}
              >
                <MenuItem value="SECCION">Por sección</MenuItem>
                <MenuItem value="CATEGORIA">Por categoría</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={inputSx}>
              <InputLabel>Sección</InputLabel>
              <Select
                label="Sección"
                value={discountForm.section_id}
                onChange={(e) => handleDiscountChange("section_id", e.target.value)}
                disabled={!formData.es_cliente}
              >
                {sections.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {discountForm.tipo === "CATEGORIA" && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth sx={inputSx}>
                <InputLabel>Categoría</InputLabel>
                <Select
                  label="Categoría"
                  value={discountForm.category_id}
                  onChange={(e) => handleDiscountChange("category_id", e.target.value)}
                  disabled={!formData.es_cliente || !discountForm.section_id}
                >
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={3}>
            <TextField
              label="Porcentaje"
              type="number"
              fullWidth
              value={discountForm.porcentaje}
              onChange={(e) => handleDiscountChange("porcentaje", e.target.value)}
              disabled={!formData.es_cliente}
              inputProps={{
                min: 0,
                max: 100,
                step: "0.01",
              }}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Fecha inicio"
              type="date"
              fullWidth
              value={discountForm.fecha_inicio}
              onChange={(e) => handleDiscountChange("fecha_inicio", e.target.value)}
              disabled={!formData.es_cliente}
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Fecha fin"
              type="date"
              fullWidth
              value={discountForm.fecha_fin}
              onChange={(e) => handleDiscountChange("fecha_fin", e.target.value)}
              disabled={!formData.es_cliente}
              InputLabelProps={{ shrink: true }}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={discountForm.activo}
                  onChange={(e) => handleDiscountChange("activo", e.target.checked)}
                  disabled={!formData.es_cliente}
                />
              }
              label="Activo"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={saveDiscount}
              disabled={!formData.es_cliente || savingDiscount}
              sx={{
                height: 40,
                textTransform: "none",
                backgroundColor: PRIMARY,
                borderRadius: 2,
                px: 1.5,
                fontWeight: 700,
                boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
                "&:hover": {
                  backgroundColor: "#0D47A1",
                },
              }}
            >
              {savingDiscount ? "Guardando..." : "Guardar descuento"}
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5 }} />

        {loadingDiscounts ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: 2.5,
              overflowX: "auto",
            }}
          >
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Tipo</TableCell>
                  <TableCell sx={headCellSx}>Sección</TableCell>
                  <TableCell sx={headCellSx}>Categoría</TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    Descuento
                  </TableCell>
                  <TableCell sx={headCellSx}>Vigencia</TableCell>
                  <TableCell sx={headCellSx}>Estado</TableCell>
                  <TableCell align="center" sx={headCellSx}>
                    Acción
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {discounts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={bodyCellSx}>
                      <Typography sx={{ py: 2, color: "#64748b", fontSize: 13 }}>
                        No hay descuentos registrados para este cliente.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {discounts.map((d) => (
                  <TableRow key={d.id} hover>
                    <TableCell sx={bodyCellSx}>
                      {d.tipo === "CATEGORIA" ? "Categoría" : "Sección"}
                    </TableCell>

                    <TableCell sx={bodyCellSx}>
                      {emptyDash(d.section_name)}
                    </TableCell>

                    <TableCell sx={bodyCellSx}>
                      {emptyDash(d.category_name)}
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{ ...bodyCellSx, fontWeight: 900, color: PRIMARY }}
                    >
                      {Number(d.porcentaje || 0).toFixed(2)}%
                    </TableCell>

                    <TableCell sx={bodyCellSx}>
                      {d.fecha_inicio || d.fecha_fin
                        ? `${d.fecha_inicio || "Sin inicio"} / ${d.fecha_fin || "Sin fin"}`
                        : "Siempre"}
                    </TableCell>

                    <TableCell sx={bodyCellSx}>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 800,
                          color: d.activo ? "#047857" : "#b91c1c",
                        }}
                      >
                        {d.activo ? "Activo" : "Inactivo"}
                      </Typography>
                    </TableCell>

                    <TableCell align="center" sx={bodyCellSx}>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlineRoundedIcon />}
                        onClick={() => deleteDiscount(d.id)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 800,
                          borderRadius: 2,
                        }}
                      >
                        Eliminar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <Paper
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid #e5e7eb",
          boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
        }}
      >
        <SectionTitle title="Información registrada" />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <InfoRow label="Nombre comercial" value={persona.nombre_comercial} />
            <InfoRow label="Identificación" value={persona.ruc || persona.cedula || "-"} strong />
            <InfoRow label="Teléfono" value={persona.telefono} />
          </Grid>

          <Grid item xs={12} md={6}>
            <InfoRow label="Correo" value={persona.correo} />
            <InfoRow label="Dirección" value={persona.direccion} />
            <InfoRow
              label="Relación"
              value={
                persona.es_cliente && persona.es_proveedor
                  ? "Cliente y proveedor"
                  : persona.es_cliente
                  ? "Cliente"
                  : persona.es_proveedor
                  ? "Proveedor"
                  : "Persona registrada"
              }
              strong
            />
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.type}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditClient;